import { useSetAtom } from 'jotai';

import useTranslation from '@/utils/hooks/translation/useTranslation';

// Lib
import { aclFeatures } from '@/lib/acl/aclFeatures';
import { MenuEntryType } from '@/lib/platform/menu/menu';
// App routes
import {
    ACCOUNT,
    ANALYZE_COMMUNITY,
    ANALYZE_CONTENT,
    ANALYZE_ENGAGEMENT,
    ANALYZE_INDUSTRY_BENCHMARK,
    ANALYZE_MENTIONS,
    ANALYZE_OVERVIEW,
    ANALYZE_REACH_IMPRESSIONS,
    CAPTIONS_LISTS,
    CONTENT_ALBUMS_COMPARATIVE_TABLE,
    CONTENT_POSTS,
    DASHBOARD,
    DASHBOARD_CUSTOMIZATION,
    DASHBOARD_GENERATE_CUSTOM_REPORT,
    DASHBOARD_VIEW,
    DATA_ACCESS,
    HOME,
    INSPIRATION,
    INTEGRATIONS,
    LISTENING_COMPETITOR,
    LISTENING_COMPETITORS,
    LISTENING_FEED,
    LISTENING_FEEDS,
    LISTENING_HASHTAG,
    LISTENING_HASHTAGS,
    MANAGE_COMMENTS_TRACKER,
    MANAGE_DM_TRACKER,
    MANAGE_IMPORTANT_TRACKER,
    MANAGE_MENTIONS_TRACKER,
    MANAGE_RESOLVED_TRACKER,
    MEDIA_LIBRARY,
    NOTIFICATIONS,
    PLATFORM_OPTIONS,
    REPORTS,
    SCHEDULER,
    SCHEDULER_COLLABORATION_NOTIFICATIONS,
    SCHEDULER_COLLABORATION_SETTINGS,
    SCHEDULER_FEED_VIEW,
    SECURITY,
    SOCIAL_PROFILES,
    SUBSCRIPTION_INVOICES,
    THEMES,
    USERS_ROLES
} from '@/lib/routes/privateRoutes';

import { ModalUpsell } from '@/components/composite/Modals/ModalUpsell';
// Components
import { menuOverlayAtom } from '@/components/layouts/LayoutPlatform/components/Menu/menuOverlayAtom';

export const useMenu = () => {
    const setMenuOverlay = useSetAtom(menuOverlayAtom);
    const { t: tCommons } = useTranslation('commons');

    /* Menu */

    const mainMenu: MenuEntryType[] = [
        // Home
        {
            id: 'home',
            icon: 'explore',
            link: HOME,
            name: tCommons('menu.home'),
            urlActiveMenu: [HOME]
        },
        // Dashboard
        {
            id: 'dashboard',
            icon: 'dashboard',
            link: DASHBOARD,
            name: tCommons('menu.dashboard'),
            urlActiveMenu: [
                DASHBOARD_VIEW,
                DASHBOARD_CUSTOMIZATION,
                DASHBOARD_GENERATE_CUSTOM_REPORT
            ]
        },
        // Analytics
        {
            id: 'analytics',
            icon: 'analytics',
            name: tCommons('menu.analytics'),
            subMenuItems: [
                {
                    id: 'analyze-overview',
                    link: ANALYZE_OVERVIEW,
                    name: tCommons('menu.overview')
                },
                {
                    id: 'analyze-engagement',
                    link: ANALYZE_ENGAGEMENT,
                    name: tCommons('menu.engagement')
                },
                {
                    id: 'analyze-community',
                    link: ANALYZE_COMMUNITY,
                    name: tCommons('menu.community')
                },
                {
                    id: 'analyze-reach-impressions',
                    link: ANALYZE_REACH_IMPRESSIONS,
                    name: tCommons('menu.reach_impressions')
                },
                {
                    id: 'analyze-content',
                    link: ANALYZE_CONTENT,
                    name: tCommons('menu.content')
                },
                {
                    id: 'analyze-mentions',
                    link: ANALYZE_MENTIONS,
                    name: tCommons('menu.mentions'),
                    acl: {
                        permissions_plan_restriction: [aclFeatures.analytic.mentions.read]
                    },
                    onClick: (
                        event: React.MouseEvent,
                        hasAcl: (permissions: string | string[]) => boolean
                    ) => {
                        // Display ModalUpsell if user doesn't have the right permissions
                        if (!hasAcl(aclFeatures.analytic.mentions.read)) {
                            event.preventDefault();
                            setMenuOverlay(
                                <ModalUpsell
                                    heading={tCommons('upsell_modal.mentions.heading')}
                                    description={tCommons('upsell_modal.mentions.description')}
                                    benefits={[
                                        {
                                            text: tCommons('upsell_modal.mentions.benefit1'),
                                            icon: 'check'
                                        },
                                        {
                                            text: tCommons('upsell_modal.mentions.benefit2'),
                                            icon: 'check'
                                        },
                                        {
                                            text: tCommons('upsell_modal.mentions.benefit3'),
                                            icon: 'check'
                                        },
                                        {
                                            text: tCommons('upsell_modal.mentions.benefit4'),
                                            icon: 'plus'
                                        }
                                    ]}
                                    illustrationPath="/images/modals/modalUpsell/mention.png"
                                    dismiss={() => setMenuOverlay('')}
                                />
                            );
                        }
                    }
                }
            ]
        },
        // Reports
        {
            id: 'reports',
            icon: 'reports',
            link: REPORTS,
            name: tCommons('menu.exports_reports')
        },
        // Content
        {
            id: 'content',
            icon: 'content',
            name: tCommons('menu.content'),
            subMenuItems: [
                {
                    id: 'content-posts',
                    link: CONTENT_POSTS,
                    name: tCommons('menu.all_posts')
                },
                {
                    id: 'content-albums',
                    link: CONTENT_ALBUMS_COMPARATIVE_TABLE,
                    name: tCommons('menu.campaigns'),
                    acl: {
                        permissions_plan_restriction: [aclFeatures.content.campaign.read]
                    },
                    onClick: (
                        event: React.MouseEvent,
                        hasAcl: (permissions: string | string[]) => boolean
                    ) => {
                        // Display ModalUpsell if user doesn't have the right permissions
                        if (!hasAcl(aclFeatures.content.campaign.read)) {
                            event.preventDefault();
                            setMenuOverlay(
                                <ModalUpsell
                                    heading={tCommons('upsell_modal.campaign.heading')}
                                    description={tCommons('upsell_modal.campaign.description')}
                                    benefits={[
                                        {
                                            text: tCommons('upsell_modal.campaign.benefit1'),
                                            icon: 'check'
                                        },
                                        {
                                            text: tCommons('upsell_modal.campaign.benefit2'),
                                            icon: 'check'
                                        },
                                        {
                                            text: tCommons('upsell_modal.campaign.benefit3'),
                                            icon: 'check'
                                        },
                                        {
                                            text: tCommons('upsell_modal.campaign.benefit4'),
                                            icon: 'plus'
                                        }
                                    ]}
                                    illustrationPath="/images/modals/modalUpsell/campaign.png"
                                    dismiss={() => setMenuOverlay('')}
                                />
                            );
                        }
                    }
                }
            ]
        },
        // Publishing
        {
            id: 'publishing',
            icon: 'publishing',
            name: tCommons('menu.publishing'),
            subMenuItems: [
                {
                    id: 'publishing-scheduler',
                    link: SCHEDULER,
                    name: tCommons('menu.calendar_view')
                },
                {
                    id: 'publishing-feed-view',
                    link: SCHEDULER_FEED_VIEW,
                    name: tCommons('menu.feed_view')
                },
                {
                    id: 'publishing-collaboration',
                    link: SCHEDULER_COLLABORATION_SETTINGS,
                    name: tCommons('menu.collaboration'),
                    urlActiveMenu: [
                        SCHEDULER_COLLABORATION_SETTINGS,
                        SCHEDULER_COLLABORATION_NOTIFICATIONS
                    ],
                    acl: {
                        permissions_plan_restriction: [
                            aclFeatures.publishing.external_collaboration.read
                        ]
                    },
                    onClick: (
                        event: React.MouseEvent,
                        hasAcl: (permissions: string | string[]) => boolean
                    ) => {
                        // Display ModalUpsell if user doesn't have the right permissions
                        if (!hasAcl(aclFeatures.publishing.external_collaboration.read)) {
                            event.preventDefault();
                            setMenuOverlay(
                                <ModalUpsell
                                    heading={tCommons('upsell_modal.collaboration.heading')}
                                    description={tCommons('upsell_modal.collaboration.description')}
                                    benefits={[
                                        {
                                            text: tCommons('upsell_modal.collaboration.benefit1'),
                                            icon: 'check'
                                        },
                                        {
                                            text: tCommons('upsell_modal.collaboration.benefit2'),
                                            icon: 'check'
                                        },
                                        {
                                            text: tCommons('upsell_modal.collaboration.benefit3'),
                                            icon: 'check'
                                        },
                                        {
                                            text: tCommons('upsell_modal.collaboration.benefit4'),
                                            icon: 'plus'
                                        }
                                    ]}
                                    illustrationPath="/images/modals/modalUpsell/collaboration.png"
                                    dismiss={() => setMenuOverlay('')}
                                />
                            );
                        }
                    }
                },
                {
                    id: 'publishing-media-library',
                    link: MEDIA_LIBRARY,
                    name: tCommons('menu.media_library')
                },
                {
                    id: 'publishing-caption-template',
                    link: CAPTIONS_LISTS,
                    name: tCommons('menu.caption_template')
                }
            ]
        },
        // AI Content Inspiration
        {
            id: 'ai-content-inspiration',
            icon: 'inspiration',
            link: INSPIRATION,
            name: tCommons('menu.inspiration')
        },
        // Comments
        {
            id: 'comments',
            icon: 'comments',
            link: MANAGE_COMMENTS_TRACKER,
            name: tCommons('menu.conversations'),
            urlActiveMenu: [
                MANAGE_COMMENTS_TRACKER,
                MANAGE_IMPORTANT_TRACKER,
                MANAGE_RESOLVED_TRACKER,
                MANAGE_MENTIONS_TRACKER,
                MANAGE_DM_TRACKER
            ]
        },
        // Listening
        {
            id: 'listening',
            icon: 'listening',
            name: tCommons('menu.listening'),
            subMenuItems: [
                {
                    id: 'listening-competitors',
                    link: LISTENING_COMPETITORS,
                    name: tCommons('menu.competitors'),
                    urlActiveMenu: [LISTENING_COMPETITOR],
                    acl: {
                        permissions_plan_restriction: [aclFeatures.listening.competitor.read]
                    },
                    onClick: (
                        event: React.MouseEvent,
                        hasAcl: (permissions: string | string[]) => boolean
                    ) => {
                        // Display ModalUpsell if user doesn't have the right permissions
                        if (!hasAcl(aclFeatures.listening.competitor.read)) {
                            event.preventDefault();
                            setMenuOverlay(
                                <ModalUpsell
                                    heading={tCommons('upsell_modal.competitors.heading')}
                                    description={tCommons('upsell_modal.competitors.description')}
                                    benefits={[
                                        {
                                            text: tCommons('upsell_modal.competitors.benefit1'),
                                            icon: 'check'
                                        },
                                        {
                                            text: tCommons('upsell_modal.competitors.benefit2'),
                                            icon: 'check'
                                        },
                                        {
                                            text: tCommons('upsell_modal.competitors.benefit3'),
                                            icon: 'check'
                                        },
                                        {
                                            text: tCommons('upsell_modal.competitors.benefit4'),
                                            icon: 'plus'
                                        }
                                    ]}
                                    illustrationPath="/images/modals/modalUpsell/hashtags.png"
                                    dismiss={() => setMenuOverlay('')}
                                />
                            );
                        }
                    }
                },
                {
                    id: 'listening-hashtags',
                    link: LISTENING_HASHTAGS,
                    name: tCommons('menu.hashtags'),
                    urlActiveMenu: [LISTENING_HASHTAG],
                    acl: {
                        permissions_plan_restriction: [aclFeatures.listening.hashtag.read]
                    },
                    onClick: (
                        event: React.MouseEvent,
                        hasAcl: (permissions: string | string[]) => boolean
                    ) => {
                        // Display ModalUpsell if user doesn't have the right permissions
                        if (!hasAcl(aclFeatures.listening.hashtag.read)) {
                            event.preventDefault();
                            setMenuOverlay(
                                <ModalUpsell
                                    heading={tCommons('upsell_modal.hashtags.heading')}
                                    description={tCommons('upsell_modal.hashtags.description')}
                                    benefits={[
                                        {
                                            text: tCommons('upsell_modal.hashtags.benefit1'),
                                            icon: 'check'
                                        },
                                        {
                                            text: tCommons('upsell_modal.hashtags.benefit2'),
                                            icon: 'check'
                                        },
                                        {
                                            text: tCommons('upsell_modal.hashtags.benefit3'),
                                            icon: 'check'
                                        },
                                        {
                                            text: tCommons('upsell_modal.hashtags.benefit4'),
                                            icon: 'plus'
                                        }
                                    ]}
                                    illustrationPath="/images/modals/modalUpsell/hashtags.png"
                                    dismiss={() => setMenuOverlay('')}
                                />
                            );
                        }
                    }
                },
                {
                    id: 'listening-feeds',
                    link: LISTENING_FEEDS,
                    name: tCommons('menu.feeds'),
                    urlActiveMenu: [LISTENING_FEED],
                    acl: {
                        permissions_plan_restriction: [aclFeatures.listening.feed.read]
                    },
                    onClick: (
                        event: React.MouseEvent,
                        hasAcl: (permissions: string | string[]) => boolean
                    ) => {
                        // Display ModalUpsell if user doesn't have the right permissions
                        if (!hasAcl(aclFeatures.listening.feed.read)) {
                            event.preventDefault();
                            setMenuOverlay(
                                <ModalUpsell
                                    heading={tCommons('upsell_modal.feeds.heading')}
                                    description={tCommons('upsell_modal.feeds.description')}
                                    benefits={[
                                        {
                                            text: tCommons('upsell_modal.feeds.benefit1'),
                                            icon: 'check'
                                        },
                                        {
                                            text: tCommons('upsell_modal.feeds.benefit2'),
                                            icon: 'check'
                                        },
                                        {
                                            text: tCommons('upsell_modal.feeds.benefit3'),
                                            icon: 'check'
                                        },
                                        {
                                            text: tCommons('upsell_modal.feeds.benefit4'),
                                            icon: 'plus'
                                        }
                                    ]}
                                    illustrationPath="/images/modals/modalUpsell/feeds.png"
                                    dismiss={() => setMenuOverlay('')}
                                />
                            );
                        }
                    }
                },
                {
                    id: 'listening-industry-benchmark',
                    link: ANALYZE_INDUSTRY_BENCHMARK,
                    name: tCommons('menu.industry_benchmark')
                }
            ]
        }
    ];

    const bottomMenu: MenuEntryType[] = [
        // Notifications
        {
            id: 'notifications',
            icon: 'notification',
            link: NOTIFICATIONS,
            name: tCommons('menu.notifications')
        },
        // Social profiles
        {
            id: 'social-profiles',
            icon: 'single-profile',
            link: SOCIAL_PROFILES,
            name: tCommons('menu.social_profiles')
        },
        // Settings
        {
            id: 'settings',
            icon: 'account',
            link: ACCOUNT,
            name: tCommons('menu.my_account'),
            urlActiveMenu: [
                ACCOUNT,
                PLATFORM_OPTIONS,
                THEMES,
                SECURITY,
                SUBSCRIPTION_INVOICES,
                DATA_ACCESS,
                INTEGRATIONS,
                USERS_ROLES
            ],
            displaySubMenuMobile: true
        }
    ];

    return {
        mainMenu,
        bottomMenu
    };
};
